import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  useTheme,
  alpha,
  IconButton,
  Tooltip,
  Button,
  Select,
  MenuItem,
} from "@mui/material";
import {
  TableRestaurant,
  LocationOn,
  CheckCircle,
  Schedule,
  Refresh,
} from "@mui/icons-material";
import { POSHeader } from "./";
import TableCard from "./TableCard";
import { DashboardStatsCard } from "./Dashboard";
import TableServices from "../../../services/TableService";
import { toast } from "react-toastify";
import { useUserWithCompany } from "../../../hooks/useCompany";
import {
  getTableStatus,
  getStatusColor,
  groupTablesByLocation,
  calculateTableStats,
} from "../utils/tableUtils";

const TablesScreen = ({
  mainTab,
  setMainTab,
  onBack,
  onPOSClick,
  handleTablesClick,
  handleOffersClick,
  isFullscreen,
  toggleFullscreen,
  fullscreenLabel,
  onHomeClick,
  onTableClick,
}) => {
  // Get company data
  const { companyId } = useUserWithCompany();

  const theme = useTheme();
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState("all");
  const [statusFilter, setStatusFilter] = useState("available");

  useEffect(() => {
    fetchTables();
  }, []);

  const fetchTables = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await TableServices.getAll({ companyId });
      if (response && response.data?.view?.data) {
        setTables(response.data.view.data);
      }
    } catch (err) {
      console.error("Error fetching tables:", err);
      setError("Failed to load tables");
      toast.error("Failed to load tables");
    } finally {
      setLoading(false);
    }
  };

  // Memoized calculations
  const tableStats = useMemo(() => calculateTableStats(tables), [tables]);
  const groupedTables = useMemo(() => groupTablesByLocation(tables), [tables]);

  // Filter tables by selected location
  const filteredGroupedTables = useMemo(() => {
    let filtered = groupedTables;

    // Filter by location
    if (selectedLocation === "all") {
      filtered = groupedTables;
    } else {
      filtered = {
        [selectedLocation]: groupedTables[selectedLocation] || [],
      };
    }

    // Filter by status
    if (statusFilter !== "all") {
      const statusFiltered = {};
      Object.keys(filtered).forEach((location) => {
        const tablesInLocation = filtered[location].filter((table) => {
          const status = getTableStatus(table);
          if (statusFilter === "available") {
            return status === "available";
          } else if (statusFilter === "occupied") {
            return status === "occupied" || status === "reserved";
          }
          return true;
        });
        if (tablesInLocation.length > 0) {
          statusFiltered[location] = tablesInLocation;
        }
      });
      return statusFiltered;
    }

    return filtered;
  }, [groupedTables, selectedLocation, statusFilter]);

  return (
    <Box
      sx={{
        height: "100%",
        bgcolor: "#f8fafc",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Header */}
      <POSHeader
        mainTab={mainTab}
        setMainTab={setMainTab}
        onPOSClick={onPOSClick}
        onTablesClick={handleTablesClick}
        onOffersClick={handleOffersClick}
        isFullscreen={isFullscreen}
        toggleFullscreen={toggleFullscreen}
        fullscreenLabel={fullscreenLabel}
        onHomeClick={onHomeClick}
      />

      {/* Main Content */}
      <Box
        sx={{
          flex: 1,
          overflow: "auto",
          p: 2,
          pb: 2,
          "&::-webkit-scrollbar": {
            width: "8px",
          },
          "&::-webkit-scrollbar-track": {
            background: "transparent",
          },
          "&::-webkit-scrollbar-thumb": {
            background: alpha(theme.palette.common.black, 0.2),
            borderRadius: "4px",
          },
        }}
      >
        {/* Stats and Filters Bar */}
        <Box
          sx={{
            mb: 2,
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          {/* Stats Cards */}
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
              <DashboardStatsCard
                title="Total Tables"
                value={tableStats.total}
                icon={TableRestaurant}
                gradient={[
                  theme.palette.primary.main,
                  theme.palette.primary.dark,
                ]}
                subtitle="All configured tables"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
              <DashboardStatsCard
                title="Available"
                value={tableStats.available}
                icon={CheckCircle}
                gradient={["#10b981", "#059669"]}
                subtitle="Empty tables"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
              <DashboardStatsCard
                title="Occupied"
                value={tableStats.occupied}
                icon={Schedule}
                gradient={["#f59e0b", "#d97706"]}
                subtitle="Tables in use"
              />
            </Grid>
          </Grid>

          {/* Compact Filter Section */}
          <Paper
            elevation={0}
            sx={{
              py: 1.5,
              px: 2,
              borderRadius: "12px",
              border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
              bgcolor: "white",
              display: "flex",
              gap: 2,
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            {/* Status Filters */}
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", flex: 1 }}>
              <Button
                size="small"
                variant={statusFilter === "all" ? "contained" : "outlined"}
                startIcon={<TableRestaurant sx={{ fontSize: 16 }} />}
                onClick={() => setStatusFilter("all")}
                sx={{
                  height: 36,
                  px: 2,
                  py: 0.5,
                  fontSize: "0.8rem",
                  fontWeight: 600,
                  borderRadius: "10px",
                  textTransform: "none",
                  ...(statusFilter === "all" && {
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                    border: "none",
                    boxShadow: `0 2px 8px ${alpha(
                      theme.palette.primary.main,
                      0.3,
                    )}`,
                  }),
                  ...(statusFilter !== "all" && {
                    color: theme.palette.text.secondary,
                    borderColor: alpha(theme.palette.divider, 0.15),
                    bgcolor: alpha(theme.palette.grey[500], 0.03),
                    "&:hover": {
                      bgcolor: alpha(theme.palette.grey[500], 0.08),
                      borderColor: alpha(theme.palette.divider, 0.25),
                    },
                  }),
                }}
              >
                All
              </Button>
              <Button
                size="small"
                variant={
                  statusFilter === "available" ? "contained" : "outlined"
                }
                startIcon={<CheckCircle sx={{ fontSize: 16 }} />}
                onClick={() => setStatusFilter("available")}
                sx={{
                  height: 36,
                  px: 2,
                  py: 0.5,
                  fontSize: "0.8rem",
                  fontWeight: 600,
                  borderRadius: "10px",
                  textTransform: "none",
                  ...(statusFilter === "available" && {
                    background:
                      "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                    border: "none",
                    boxShadow: "0 2px 8px rgba(16, 185, 129, 0.3)",
                  }),
                  ...(statusFilter !== "available" && {
                    color: theme.palette.text.secondary,
                    borderColor: alpha(theme.palette.divider, 0.15),
                    bgcolor: alpha(theme.palette.grey[500], 0.03),
                    "&:hover": {
                      bgcolor: alpha(theme.palette.grey[500], 0.08),
                      borderColor: alpha(theme.palette.divider, 0.25),
                    },
                  }),
                }}
              >
                Available ({tableStats.available})
              </Button>
              <Button
                size="small"
                variant={statusFilter === "occupied" ? "contained" : "outlined"}
                startIcon={<Schedule sx={{ fontSize: 16 }} />}
                onClick={() => setStatusFilter("occupied")}
                sx={{
                  height: 36,
                  px: 2,
                  py: 0.5,
                  fontSize: "0.8rem",
                  fontWeight: 600,
                  borderRadius: "10px",
                  textTransform: "none",
                  ...(statusFilter === "occupied" && {
                    background:
                      "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                    border: "none",
                    boxShadow: "0 2px 8px rgba(245, 158, 11, 0.3)",
                  }),
                  ...(statusFilter !== "occupied" && {
                    color: theme.palette.text.secondary,
                    borderColor: alpha(theme.palette.divider, 0.15),
                    bgcolor: alpha(theme.palette.grey[500], 0.03),
                    "&:hover": {
                      bgcolor: alpha(theme.palette.grey[500], 0.08),
                      borderColor: alpha(theme.palette.divider, 0.25),
                    },
                  }),
                }}
              >
                Occupied ({tableStats.occupied})
              </Button>
            </Box>

            {/* Location Filter */}
            <Box
              sx={{
                display: "flex",
                gap: 1,
                alignItems: "center",
                borderLeft: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                pl: 2,
              }}
            >
              <LocationOn
                sx={{
                  fontSize: 18,
                  color: theme.palette.text.secondary,
                }}
              />
              <Select
                value={selectedLocation}
                onChange={(e) => setSelectedLocation(e.target.value)}
                size="small"
                sx={{
                  height: 36,
                  minWidth: 160,
                  fontSize: "0.8rem",
                  fontWeight: 600,
                  borderRadius: "10px",
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: alpha(theme.palette.divider, 0.15),
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: alpha(theme.palette.divider, 0.25),
                  },
                }}
              >
                <MenuItem value="all">All Locations</MenuItem>
                {Object.keys(groupedTables).map((location) => (
                  <MenuItem key={location} value={location}>
                    {location} ({groupedTables[location].length})
                  </MenuItem>
                ))}
              </Select>
            </Box>

            {/* Refresh Button */}
            <Tooltip title="Refresh Tables">
              <IconButton
                onClick={fetchTables}
                disabled={loading}
                size="small"
                sx={{
                  width: 36,
                  height: 36,
                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                  border: `1px solid ${alpha(theme.palette.divider, 0.15)}`,
                  "&:hover": {
                    bgcolor: alpha(theme.palette.primary.main, 0.15),
                  },
                  "&:disabled": {
                    bgcolor: alpha(theme.palette.grey[500], 0.05),
                  },
                }}
              >
                <Refresh sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>
          </Paper>
        </Box>

        {/* Loading State */}
        {loading && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              py: 8,
            }}
          >
            <CircularProgress />
          </Box>
        )}

        {/* Error State */}
        {error && !loading && (
          <Alert
            severity="error"
            sx={{
              mb: 2,
              borderRadius: "12px",
            }}
          >
            {error}
          </Alert>
        )}

        {/* Tables Content */}
        {!loading && !error && (
          <>
            {Object.keys(filteredGroupedTables).length === 0 ? (
              <Paper
                elevation={0}
                sx={{
                  p: 6,
                  textAlign: "center",
                  borderRadius: "12px",
                  border: `2px dashed ${alpha(theme.palette.divider, 0.3)}`,
                  bgcolor: "white",
                }}
              >
                <TableRestaurant
                  sx={{
                    fontSize: 60,
                    color: theme.palette.text.secondary,
                    mb: 2,
                  }}
                />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No Tables Found
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {selectedLocation === "all"
                    ? "There are no tables configured yet."
                    : `No tables found in ${selectedLocation}.`}
                </Typography>
              </Paper>
            ) : (
              <Box>
                {Object.entries(filteredGroupedTables).map(
                  ([location, locationTables]) => (
                    <Box key={location} sx={{ mb: 3 }}>
                      {selectedLocation === "all" && (
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 700,
                            mb: 2,
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            fontSize: "1rem",
                            color: theme.palette.text.secondary,
                          }}
                        >
                          <LocationOn sx={{ fontSize: 18 }} />
                          {location}
                          <Chip
                            label={`${locationTables.length} tables`}
                            size="small"
                            sx={{
                              ml: 1,
                              height: 24,
                              fontSize: "0.75rem",
                              fontWeight: 600,
                            }}
                          />
                        </Typography>
                      )}

                      <Grid container spacing={2}>
                        {locationTables.map((table) => {
                          const status = getTableStatus(table);
                          const statusInfo = getStatusColor(status, theme);
                          const activeOrder = table.Orders?.[0];

                          return (
                            <Grid
                              size={{ xs: 12, sm: 6, md: 4, lg: 3, xl: 2 }}
                              key={table.id}
                            >
                              <TableCard
                                table={table}
                                status={status}
                                statusInfo={statusInfo}
                                activeOrder={activeOrder}
                                onClick={() => {
                                  if (status === "occupied" || status === "reserved") {
                                    toast.warning(`Table ${table.name || table.tableNumber} is already occupied.`);
                                    return;
                                  }
                                  onTableClick && onTableClick(table);
                                }}
                              />
                            </Grid>
                          );
                        })}
                      </Grid>
                    </Box>
                  ),
                )}
              </Box>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

export default TablesScreen;
